/**
 * OPVisual's show palettes as a list to pick from (2026-10-28, OPVisual's ask; operator: "visualiser palette that
 * should be controllable by opbuttons"). Each row is the palette's name and its four swatches, dark to bright, read from
 * what OPVisual itself reports (`state.palettes`, `opvisual/live.ts`); the one on now is marked. Used by the "Show
 * palette" OPVisual action in the Inspector and by a timeline's palette step.
 *
 * Until an OPVisual that sends its palettes has reported, the typed field stays as the fallback (a name OPVisual does
 * not know is ignored there, so the list is the way in).
 */
import { For, Show, onCleanup } from 'solid-js';
import { opvLive, opvLiveUsable } from '../opvisual/live.ts';
import { watchOpvListing } from '../opvisual/listing.ts';
import { bridges } from '../opbridge/store.ts';

export function OpvPalettePicker(props: {
  /** The machine OPVisual runs on (a bridge row id), or undefined for none set. */
  target: string | undefined;
  /** The palette chosen (an id, or a typed name / number from before the list). */
  value: string;
  onPick: (id: string, name: string) => void;
}) {
  // Keeps OPBridge's remembered `state` coming in while the list is on screen (the socket alone may be on another PC).
  // With no computer set for OPVisual (two Windows PCs leave it unset, 2026-10-28), every paired computer is read and the
  // first whose OPVisual sent its palettes is used: the palettes are OPVisual's own list, the same on every PC.
  const watched = props.target ? [props.target] : bridges().map((b) => b.id);
  for (const id of watched) onCleanup(watchOpvListing(id));
  const live = () => {
    if (props.target) return opvLive(props.target);
    for (const id of watched) { const l = opvLive(id); if (l && l.palettes.length > 0) return l; }
    return null;
  };
  const palettes = () => live()?.palettes ?? [];
  const onNow = () => { const l = live(); return l && opvLiveUsable(l) ? l.palette : null; };
  const chosen = (id: string, name: string) => {
    const v = props.value.trim().toLowerCase();
    return v !== '' && (v === id.toLowerCase() || v === name.toLowerCase());
  };
  return (
    <Show
      when={palettes().length > 0}
      fallback={(
        <div class="opv-pal-fallback">
          <input
            type="text"
            class="opv-pal-typed"
            placeholder="Neon Noir, or its number"
            value={props.value}
            onChange={(e) => props.onPick(e.currentTarget.value, e.currentTarget.value)}
          />
          <span class="opv-pal-note">{bridges().length > 0 ? "OPVisual hasn't sent its palettes yet, so type one." : 'No computer is paired yet, so type one.'}</span>
        </div>
      )}
    >
      <div class="opv-pal" role="listbox" aria-label="Palette">
        <For each={palettes()}>
          {(p) => (
            <button
              type="button"
              role="option"
              class="opv-pal-row"
              data-palette={p.id}
              aria-selected={chosen(p.id, p.name)}
              data-on-now={onNow() === p.id ? 'true' : undefined}
              onClick={() => props.onPick(p.id, p.name)}
            >
              <span class="opv-pal-sw" aria-hidden="true">
                <For each={p.swatch}>{(c) => <i style={{ background: c }} />}</For>
              </span>
              <span class="opv-pal-name">{p.name}</span>
              <Show when={onNow() === p.id}><span class="opv-pal-now">On now</span></Show>
            </button>
          )}
        </For>
      </div>
    </Show>
  );
}
